import {
	useState,
	useEffect,
	useRef,
} from 'react';

export enum ListOverflow {
	Neither = 'neither',
	Top = 'top',
	Bottom = 'bottom',
	Both = 'both',
}

const useScrollListOverflow = (debounceTime = 100) => {
	const [showListOverflow, setShowListOverflow] = useState<ListOverflow>(ListOverflow.Neither);

	const scrollList = useRef<HTMLDivElement>(null);
	const scrollDebounce = useRef<NodeJS.Timeout | null>(null);

	const debouncedScroll = (): void => {
		if (scrollDebounce.current) {
			clearTimeout(scrollDebounce.current);
		}

		scrollDebounce.current = setTimeout(() => {
			if (!scrollList.current) {
				return;
			}

			const {
				offsetHeight,
				scrollHeight,
				scrollTop,
			} = scrollList.current;
			const percentage = scrollTop / (scrollHeight - offsetHeight);

			if (offsetHeight >= scrollHeight) {
				setShowListOverflow(ListOverflow.Neither);
			} else if (percentage === 0) {
				setShowListOverflow(ListOverflow.Top);
			} else if (percentage === 1) {
				setShowListOverflow(ListOverflow.Bottom);
			} else {
				setShowListOverflow(ListOverflow.Both);
			}
		}, debounceTime);
	};

	useEffect(() => {
		if (scrollList.current) {
			scrollList.current.addEventListener('scroll', debouncedScroll);
			window.addEventListener('resize', debouncedScroll);
		}

		return () => {
			if (scrollList.current) {
				scrollList.current.removeEventListener('scroll', debouncedScroll);
				window.removeEventListener('resize', debouncedScroll);
			}
		};
	}, [scrollList.current]);

	// run on init
	useEffect(() => {
		debouncedScroll();
	}, []);

	return {
		showTop: showListOverflow === ListOverflow.Top,
		showBottom: showListOverflow === ListOverflow.Bottom,
		showBoth: showListOverflow === ListOverflow.Both,
		scrollList,
	};
};

export default useScrollListOverflow;
